import React, { useState } from 'react';
import CodeInput from './CodeInput';
import PlatformMessagesBanner from './PlatformMessagesBanner';
import { api } from '../services/api';
import { getReturnToParam } from '../utils/host';
import finbaseLogo from '../assets/logos/finbase-logo.svg';

const PasswordReset = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(new URLSearchParams(window.location.search).get('email') || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const returnTo = getReturnToParam();

  const handleRequest = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setLoading(true);
    try {
      await api.post('/acct-mgmt/v1/password/reset/request', { email });
      setStep('verify');
      setStatus({ type: 'success', message: 'Reset code sent. Check your email.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to send reset code.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    if (password !== confirmPassword) {
      setStatus({ type: 'danger', message: 'Passwords do not match.' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/acct-mgmt/v1/password/reset/verify', {
        email,
        code,
        password,
        confirm_password: confirmPassword,
      });
      setStep('done');
      setStatus({ type: 'success', message: 'Password updated. You can now sign in.' });
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to reset password.' });
      setCode('');
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <PlatformMessagesBanner surface="id_portal" />
      <div className="card auth-card shadow-sm">
        <div className="auth-logo">
          <img src={finbaseLogo} alt="Finbase" />
        </div>
        <h1 className="h3 fw-bold text-dark mb-2 text-center">Reset your password</h1>
        <p className="text-muted mb-4 text-center">We’ll send a verification code to your email.</p>
        {status.message && <div className={`alert alert-${status.type}`}>{status.message}</div>}

        {step === 'email' && (
          <form onSubmit={handleRequest}>
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {loading ? 'Sending...' : 'Send Code'}
            </button>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <div className="mb-3 text-center">
              <label className="form-label text-center d-block">Verification Code</label>
              <CodeInput value={code} onChange={setCode} shake={shake} autoFocus />
            </div>
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <label className="form-label mt-2">Confirm New Password</label>
              <input
                type="password"
                className="form-control mt-2"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={loading || code.length < 6}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}

        {step === 'done' && (
          <a href={`/login?return_to=${encodeURIComponent(returnTo)}`} className="btn btn-outline-primary w-100">Back to Login</a>
        )}
        <div className="auth-footer">Finbase Single Sign-On: one account for all Finbase apps.</div>
      </div>
    </div>
  );
};

export default PasswordReset;
